import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Buscador, Chip, Filtro, IconoMas, Listado, useBusquedaDiferida, type Columna, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { CambiarCodigoPersona } from './CambiarCodigoPersona';
import { EliminarPersona } from './EliminarPersona';
import {
  nombreDe,
  useCambiosPersona,
  useEspecialidades,
  usePersonal,
  useSucursalesDelTaller,
  type FiltrosPersonal,
  type Persona,
} from './datos';

// El listado del personal (#58): «Mecánicos/Operadores - Listado» del Figma y sus
// variantes (Menú de fila), (Sin resultados) y (Sin personal). Sigue a
// `ListadoProveedores.tsx`.
//
// **Sin la columna «Acceso» del Figma**: el acceso al sistema —rol, PIN, QR— es
// de #208, en la fase 4. Tampoco «Importar desde archivo» en el vacío: no hay
// importación de personal.
//
// La fila abre la ficha (`FichaPersona`). Alta y edición van en su propia página
// (`FormularioPersona`). Eliminar y cambiar el código, en ventanas sobre el listado.
//
// **Los filtros viven en la URL** (`?buscar=&especialidad=&sucursal=&estado=`),
// como en Proveedores: «Atrás» desde la ficha los trae de vuelta.

/** Los filtros de la URL. Los parámetros se llaman como los campos. */
type FiltrosUrl = Omit<FiltrosPersonal, 'cursor'>;

/** Un valor que no se reconoce —un enlace viejo o editado a mano— cuenta como «Todos». */
function leerFiltros(params: URLSearchParams): FiltrosUrl {
  const estado = params.get('estado');
  return {
    buscar: params.get('buscar') ?? '',
    especialidad: params.get('especialidad') ?? '',
    sucursal: params.get('sucursal') ?? '',
    estado: estado === 'activos' || estado === 'inactivos' ? estado : 'todos',
  };
}

export function ListadoPersonal() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosPersona();
  const especialidades = useEspecialidades();
  const sucursales = useSucursalesDelTaller();
  useTituloPantalla(t('admin.personal.titulo'));

  const [params, setParams] = useSearchParams();
  const url = leerFiltros(params);
  const busqueda = params.toString();

  const [texto, setTexto] = useState(url.buscar);
  const [cursor, setCursor] = useState<string | null>(null);
  // Por dónde se pasó: la API solo sabe ir hacia adelante.
  const [anteriores, setAnteriores] = useState<(string | null)[]>([]);
  const [eliminando, setEliminando] = useState<Persona | null>(null);
  const [cambiandoCodigo, setCambiandoCodigo] = useState<Persona | null>(null);
  const { hacer, problema } = useAccionDeFila();

  // Cuando la URL cambia —un filtro, «Atrás», o tocar el menú, que la deja
  // limpia— se vuelve a la primera página. Se ajusta al dibujar, como en
  // `ListadoProveedores`, y no en un efecto que dibujaría primero lo viejo.
  const [vista, setVista] = useState({ busqueda, buscar: url.buscar });
  if (busqueda !== vista.busqueda) {
    setVista({ busqueda, buscar: url.buscar });
    setCursor(null);
    setAnteriores([]);
    if (url.buscar !== vista.buscar) setTexto(url.buscar);
  }

  /** Reemplaza la entrada del historial: «Atrás» sale del listado, no deshace un filtro por tecla. */
  function filtrar(cambio: Partial<FiltrosUrl>) {
    setParams(
      (actuales) => {
        const nuevos = new URLSearchParams(actuales);
        for (const [clave, valor] of Object.entries(cambio)) {
          // «Todos» es el valor del combo de estado; los demás usan vacío.
          if (valor && valor !== 'todos') nuevos.set(clave, valor);
          else nuevos.delete(clave);
        }
        return nuevos;
      },
      { replace: true },
    );
  }

  useBusquedaDiferida(texto, (buscar) => filtrar({ buscar }), { pendiente: texto !== url.buscar });

  const filtros: FiltrosPersonal = { ...url, cursor };
  const consulta = usePersonal(filtros);
  const pagina = consulta.data;

  // El nombre de cada sucursal, por id: la fila trae solo los ids.
  const nombreSucursal = new Map((sucursales.data ?? []).map((s) => [String(s.id), s.name]));

  // `desdeListado`: la ficha y el formulario vuelven con «Atrás», y el listado
  // regresa con sus filtros.
  const verFicha = (p: Persona) => void navegar(`/personal/${String(p.id)}`, { state: { desdeListado: true } });
  const editar = (p: Persona) => void navegar(`/personal/${String(p.id)}/editar`, { state: { desdeListado: true } });

  function opciones(p: Persona): ElementoMenu[] {
    const id = String(p.id);
    const nombre = { nombre: nombreDe(p) };

    return [
      { etiqueta: t('admin.personal.menu.verFicha'), onSeleccionar: () => verFicha(p) },
      { etiqueta: t('admin.personal.menu.editar'), onSeleccionar: () => editar(p) },
      { etiqueta: t('admin.personal.menu.cambiarCodigo'), onSeleccionar: () => setCambiandoCodigo(p) },
      p.isActive
        ? {
            etiqueta: t('admin.personal.menu.desactivar'),
            onSeleccionar: () =>
              void hacer(cambios.cambiarEstado(id, false), t('admin.personal.aviso.desactivado', nombre)),
          }
        : {
            etiqueta: t('admin.personal.menu.activar'),
            onSeleccionar: () => void hacer(cambios.cambiarEstado(id, true), t('admin.personal.aviso.activado', nombre)),
          },
      { tipo: 'divisor' as const },
      { etiqueta: t('admin.personal.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminando(p) },
    ];
  }

  const columnas: Columna<Persona>[] = [
    {
      id: 'codigo',
      titulo: t('admin.personal.columna.codigo'),
      ancho: 110,
      // Un botón, para llegar con el teclado: la fila entera solo responde al dedo y al mouse.
      celda: (p) => (
        <button type="button" className="tipos__enlace" onClick={() => verFicha(p)}>
          {p.employeeCode}
        </button>
      ),
      tarjeta: 'pie',
    },
    { id: 'nombre', titulo: t('admin.personal.columna.nombre'), celda: nombreDe, tarjeta: 'titulo' },
    {
      id: 'especialidades',
      titulo: t('admin.personal.columna.especialidades'),
      ancho: 220,
      // «Frenos · Motor», como en el Figma.
      celda: (p) => (p.specialties.length > 0 ? p.specialties.join(' · ') : '—'),
      tarjeta: 'pie',
    },
    {
      id: 'sucursales',
      titulo: t('admin.personal.columna.sucursales'),
      ancho: 160,
      celda: (p) =>
        p.locations
          .map((l) => nombreSucursal.get(String(l)))
          .filter(Boolean)
          .join(' · ') || '—',
    },
    { id: 'telefono', titulo: t('admin.personal.columna.telefono'), ancho: 130, celda: (p) => p.phone ?? '—' },
    {
      id: 'estado',
      titulo: t('admin.personal.columna.estado'),
      ancho: 120,
      celda: (p) =>
        p.isActive ? <Chip tono="exito">{t('admin.personal.activo')}</Chip> : <Chip>{t('admin.personal.inactivo')}</Chip>,
      tarjeta: 'esquina',
    },
  ];

  const botonNuevo = (
    <Boton icono={<IconoMas />} onClick={() => void navegar('/personal/nuevo')}>
      {t('admin.personal.nuevo')}
    </Boton>
  );

  const hayFiltros =
    filtros.buscar !== '' ||
    filtros.especialidad !== '' ||
    filtros.sucursal !== '' ||
    filtros.estado !== 'todos' ||
    filtros.cursor !== null;

  return (
    <>
      {problema && <AvisoError aviso={{ titulo: t('admin.personal.error.titulo'), texto: problema }} />}

      <Listado
        titulo={t('admin.personal.titulo')}
        info={t('admin.personal.info')}
        columnas={columnas}
        filas={pagina?.items}
        claveFila={(p) => String(p.id)}
        estado={consulta.isPending ? 'cargando' : consulta.isError ? 'error' : 'listo'}
        onReintentar={() => void consulta.refetch()}
        filtros={
          <>
            <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.personal.buscar')} />
            {/* Los dos combos solo aparecen si hay de qué elegir. */}
            {(especialidades.data?.length ?? 0) > 0 && (
              <Filtro
                etiqueta={t('admin.personal.filtro.especialidad')}
                valor={filtros.especialidad}
                onCambiar={(especialidad) => filtrar({ especialidad })}
                opciones={[
                  { valor: '', etiqueta: t('admin.personal.filtro.todas') },
                  ...(especialidades.data ?? []).map((e) => ({ valor: String(e.id), etiqueta: e.name })),
                ]}
              />
            )}
            {(sucursales.data?.length ?? 0) > 1 && (
              <Filtro
                etiqueta={t('admin.personal.filtro.sucursal')}
                valor={filtros.sucursal}
                onCambiar={(sucursal) => filtrar({ sucursal })}
                opciones={[
                  { valor: '', etiqueta: t('admin.personal.filtro.todas') },
                  ...(sucursales.data ?? []).map((s) => ({ valor: String(s.id), etiqueta: s.name })),
                ]}
              />
            )}
            <Filtro
              etiqueta={t('admin.personal.filtro.estado')}
              valor={filtros.estado}
              onCambiar={(valor) => filtrar({ estado: valor as FiltrosPersonal['estado'] })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.personal.filtro.todos') },
                { valor: 'activos', etiqueta: t('admin.personal.filtro.activos') },
                { valor: 'inactivos', etiqueta: t('admin.personal.filtro.inactivos') },
              ]}
            />
          </>
        }
        acciones={botonNuevo}
        // Una página que no es la primera cuenta como filtro: si queda vacía, el
        // taller no está vacío. Ver `ListadoSucursales.tsx`.
        hayFiltros={hayFiltros}
        onLimpiarFiltros={() => {
          setTexto('');
          setCursor(null);
          setAnteriores([]);
          setParams({}, { replace: true });
        }}
        vacio={{
          titulo: t('admin.personal.vacio.titulo'),
          texto: t('admin.personal.vacio.texto'),
          acciones: botonNuevo,
        }}
        {...(filtros.buscar && {
          detalleSinResultados: t('admin.personal.sinResultados', { texto: filtros.buscar }),
        })}
        opcionesFila={opciones}
        onFila={verFicha}
        paginacion={{
          hayAnterior: anteriores.length > 0,
          hayMas: pagina?.hasMore ?? false,
          onAnterior: () => {
            setCursor(anteriores.at(-1) ?? null);
            setAnteriores((a) => a.slice(0, -1));
          },
          onSiguiente: () => {
            if (!pagina?.nextCursor) return;
            setAnteriores((a) => [...a, cursor]);
            setCursor(pagina.nextCursor);
          },
        }}
      />

      <EliminarPersona persona={eliminando} onCerrar={() => setEliminando(null)} />
      <CambiarCodigoPersona persona={cambiandoCodigo} onCerrar={() => setCambiandoCodigo(null)} />
    </>
  );
}
